import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const CreateBookingModal = ({
  isOpen,
  onClose,
  onSubmit,
  apartmentId,
  setApartmentId,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  apartments,
  error,
}) => {
  const { t } = useTranslation();
  const [availabilityError, setAvailabilityError] = useState("");
  const [totalPrice, setTotalPrice] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const [isAvailable, setIsAvailable] = useState(false);
  const paypalButtonContainerRef = useRef(null);

  // Auto-select when there is exactly one apartment
  useEffect(() => {
    const entries = Object.entries(apartments);
    if (entries.length === 1 && !apartmentId) {
      setApartmentId(entries[0][0]);
    }
  }, [apartments, apartmentId, setApartmentId]);

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  useEffect(() => {
    if (isAvailable && totalPrice && window.paypal && paypalButtonContainerRef.current) {
      console.log('Rendering PayPal button with price:', totalPrice);
      
      // Clear previous buttons if any
      paypalButtonContainerRef.current.innerHTML = '';

      window.paypal.Buttons({
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [{
              amount: {
                value: totalPrice.toFixed(2),
                currency_code: 'USD',
              },
              payee: {
                email_address: process.env.PAYPAL_EMAIL,
              },
            }],
          });
        },
        onApprove: (data, actions) => {
          return actions.order.capture().then(details => {
            alert(t('transaction_completed') + ' ' + details.payer.name.given_name);
            onSubmit();
          });
        },
        onError: (err) => {
          console.error('PayPal Checkout onError', err);
          alert(t('transaction_error'));
        },
      }).render(paypalButtonContainerRef.current);
    }
  }, [isAvailable, totalPrice, onSubmit, t]);

  if (!isOpen) return null;

  const fetchCurrencySymbol = async (currencyId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/currencies/${currencyId}/`);
      if (!response.ok) {
        throw new Error(t('failed_to_fetch_currency'));
      }
      const data = await response.json();
      return data.symbol;
    } catch (error) {
      console.error(t('error_fetching_currency_symbol'), error);
      return "$";
    }
  };

  const calculateTotalPrice = async (apartmentId, startDate, endDate) => {
    try {
      // Fetch daily prices for the apartment
      const response = await fetch(`${getApiBaseUrl()}/api/properties/apartments/${apartmentId}/daily-prices/`);
      if (!response.ok) {
        throw new Error(t('failed_to_fetch_daily_prices'));
      }
  
      const dailyPrices = await response.json();
  
      // Convert startDate and endDate to Date objects
      const start = new Date(startDate);
      const end = new Date(endDate);
  
      // Filter daily prices for the specified date range
      const filteredPrices = dailyPrices.filter((priceEntry) => {
        const priceDate = new Date(priceEntry.date);
        return priceDate >= start && priceDate <= end;
      });
  
      // Sum up the prices for the filtered dates
      const totalPrice = filteredPrices.reduce((sum, priceEntry) => sum + priceEntry.price, 0);
  
      // Fetch the currency symbol
      const currencySymbol = await fetchCurrencySymbol(dailyPrices[0]?.currency);
  
      console.log('Calculated total price:', totalPrice);
      setCurrencySymbol(currencySymbol);
      return totalPrice || 0; // Ensure totalPrice is always a number
    } catch (error) {
      console.error(t('error_fetching_daily_prices'), error);
      return 0; // Return 0 in case of an error
    }
  };

  const checkAvailability = async () => {
    setAvailabilityError("");
    setTotalPrice(null);
    setIsAvailable(false);
  
    try {
      // Check existing bookings
      const bookingsResponse = await fetch(
        `${getApiBaseUrl()}/api/properties/apartments/${apartmentId}/bookings/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        }
      );
  
      if (!bookingsResponse.ok) throw new Error(t('failed_to_check_availability'));
  
      const existingBookings = await bookingsResponse.json();
      console.log("Existing bookings:", existingBookings);
  
      // Check for date conflicts
      const newStart = new Date(startDate);
      const newEnd = new Date(endDate);
  
      const hasConflict = existingBookings.some(booking => {
        const bookingStart = new Date(booking.start_date);
        const bookingEnd = new Date(booking.end_date);
        return (
          (newStart >= bookingStart && newStart <= bookingEnd) ||
          (newEnd >= bookingStart && newEnd <= bookingEnd) ||
          (newStart <= bookingStart && newEnd >= bookingEnd)
        );
      });
  
      if (hasConflict) {
        setAvailabilityError(t('no_availability'));
        return;
      }
  
      // Calculate price if available
      const price = await calculateTotalPrice(apartmentId, startDate, endDate);
      if (price > 0) {
        setTotalPrice(price);
        setIsAvailable(true);
      } else {
        setAvailabilityError(t('failed_to_calculate_price'));
      }
    } catch (error) {
      console.error(t('error_checking_availability'), error);
      setAvailabilityError(error.message || t('failed_to_check_availability'));
    }
  };

  
  const handleCancel = () => {
    setIsAvailable(false);
    setTotalPrice(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded-lg w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">{t('check_availability')}</h2>
        
        <div className="mb-4">
          <label className="block text-gray-700">{t('apartment')}</label>
          <select
            value={apartmentId}
            onChange={(e) => setApartmentId(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          >
            <option value="">{t('select_apartment')}</option>
            {Object.entries(apartments).map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700">{t('start_date')}</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-700">{t('end_date')}</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          />
        </div>

        {availabilityError && (
          <p className="text-red-500 mb-4">{availabilityError}</p>
        )}

        {isAvailable && totalPrice && (
          <div className="mb-4">
            <p className="text-green-500 mb-4">
              {t('total_price')}: {currencySymbol}{totalPrice.toFixed(2)}
            </p>
            <div 
              id="paypal-button-container" 
              ref={paypalButtonContainerRef}
              className="w-full"
              style={{ minHeight: '40px' }}
            ></div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleCancel}
            className="bg-gray-500 text-white px-4 py-2 rounded-md mr-2 hover:bg-gray-600"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={checkAvailability}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
            disabled={!apartmentId || !startDate || !endDate}
          >
            {t('check_availability')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateBookingModal;